import { DialogContent, DialogProps } from '@material-ui/core';
import { FormSelectOrCreateCompanyFieldWithSelector } from 'app/main/contacts/companies/forms/FormSelectOrCreateCompanyFieldWithSelector';
import { Form, Formik, FormikHelpers, useFormikContext } from 'formik';
import { FullScreenDialogWithStepper } from 'framework/dialogs/FullScreenDialogWithStepper';
import { FormAutocompleteField } from 'framework/forms/FormAutocompleteField';
import { FormDatePickerWithUnit } from 'framework/forms/FormDatePickerWithUnit';
import { FormSingleCheckboxField } from 'framework/forms/FormSingleCheckboxField';
import { FormTextField } from 'framework/forms/FormTextField';
import { PageableFormDialogActions } from 'framework/forms/PageableFormDialogActions';
import { handleFormResponse } from 'framework/forms/utils/handleFormResponse';
import { setLineErrors } from 'framework/forms/utils/setLineErrors';
import { useApiEffect } from 'framework/hooks/useApiEffect';
import { useFormSubmit } from 'framework/hooks/useFormSubmit';
import { useSnackbarNotify } from 'framework/hooks/useSnackbarNotify';
import {
	IAdvanceInvoiceModel,
	ILocationSummary,
	salesCommand_newAdvanceInvoice,
	salesQuery_nextInvoiceReference,
	salesQuery_settings,
	ValidUntilTimeUnits,
} from 'gen/ApiClient';
import { IStrings } from 'localization/IStrings';
import { useLocalization } from 'localization/useLocalization';
import React, { useMemo, useState } from 'react';
import { FormRecipientField } from 'shared/forms/FormRecipientField';
import { useNextReferenceInterceptor } from 'shared/interceptors/useNextReferenceInterceptor';
import { useFromLocationInterceptor } from 'shared/searchAndSelect/useFromLocationInterceptor';
import { useWithRecipientInterceptor } from 'shared/searchAndSelect/useWithRecipientInterceptor';
import * as yup from 'yup';
import { SearchProvider } from '../invoice/context/SearchProvider';
import { SaleFormConfirmation } from '../SaleFormConfirmation';
import { ILine } from './ILine';
import { SearchAndSelectProductsForAdvanceInvoice } from './SearchAndSelectProductsForAdvanceInvoice';
import { lineToModel } from './utils/lineToModel';

const createSchema = (strings: IStrings, mustSelectCompany: boolean) => {
	return yup
		.object<IAdvanceInvoiceModel>({
			validUntil: yup.date().required(),
			lines: yup.mixed(),
			reference: yup.string().required(),
			isAutoSelectReference: yup.boolean().defined(),
			patientId: yup.string(),
			zip: yup.string(),
			addressLine: yup.string(),
			city: yup.string(),
			companyId: mustSelectCompany ? yup.string().required() : yup.string(),
			country: yup.string(),
			recipientName: yup.string().required(),
			fromLocationId: yup.string().required(strings.location),
		})
		.defined();
};

const emptyValues: IAdvanceInvoiceModel = {
	lines: [],
	reference: '',
	isAutoSelectReference: true,
	patientId: '',
	city: '',
	companyId: '',
	addressLine: '',
	zip: '',
	country: '',
	recipientName: '',
	validUntil: undefined as any,
	fromLocationId: '',
};

const stepsRecord: Record<number, (keyof IAdvanceInvoiceModel)[]> = {
	0: ['reference', 'isAutoSelectReference', 'validUntil', 'fromLocationId'],
	1: ['companyId'],
	2: ['recipientName', 'addressLine', 'city', 'zip', 'country'],
	3: ['lines'],
	4: [],
};

interface IProps extends DialogProps {
	confirm: (id: string) => void;
	cancel: VoidFunction;
	patientId?: string;
}

export const AdvanceInvoiceForm = ({ confirm, cancel, patientId, ...rest }: IProps) => {
	const strings = useLocalization();
	const [lines, setLines] = useState<ILine[]>([]);
	const [step, setStep] = useState<number>(0);
	const [isSelectCompany, setIsSelectCompany] = useState<boolean>(patientId === undefined);
	const schema = useMemo(() => createSchema(strings, patientId === undefined || isSelectCompany), [strings, patientId, isSelectCompany]);
	const [create, isSubmitting] = useFormSubmit(salesCommand_newAdvanceInvoice);
	const notify = useSnackbarNotify();

	const handleSubmit = async (values: IAdvanceInvoiceModel, helpers: FormikHelpers<IAdvanceInvoiceModel>) => {
		if (lines.length === 0) {
			notify(strings.selectAtLeastOneWhat(strings.line), 'error');
		} else {
			const r = await create({ ...values, lines: lines.map((t, index) => lineToModel(t, index)) });
			if (handleFormResponse(r, helpers, stepsRecord, setStep)) {
				notify('Voorschotfactuur geregistreerd');
				confirm(r.result.objectId);
			} else {
				setLineErrors('lines', r, lines, setLines);
			}
		}
	};

	return (
		<Formik<IAdvanceInvoiceModel>
			validateOnMount
			initialValues={{ ...emptyValues, patientId: patientId }}
			validationSchema={schema}
			onSubmit={handleSubmit}>
			<Form>
				<SearchProvider>
					<InnerDialog
						{...rest}
						isSubmitting={isSubmitting}
						cancel={cancel}
						lines={lines}
						setLines={setLines}
						step={step}
						setStep={setStep}
						stepsRecord={stepsRecord}
						schema={schema}
						isSelectCompany={isSelectCompany}
						setIsSelectCompany={setIsSelectCompany}
						patientId={patientId}
					/>
				</SearchProvider>
			</Form>
		</Formik>
	);
};

interface IInnerProps extends DialogProps {
	cancel: VoidFunction;
	lines: ILine[];
	setLines: React.Dispatch<React.SetStateAction<ILine[]>>;
	isSubmitting: boolean;
	step: number;
	setStep: React.Dispatch<React.SetStateAction<number>>;
	stepsRecord: Record<number, (keyof IAdvanceInvoiceModel)[]>;
	schema: yup.ObjectSchema<IAdvanceInvoiceModel>;
	isSelectCompany: boolean;
	setIsSelectCompany: React.Dispatch<React.SetStateAction<boolean>>;
	patientId: string | undefined;
}

export const InnerDialog = ({
	cancel,
	lines,
	setLines,
	isSubmitting,
	step,
	setStep,
	stepsRecord,
	schema,
	isSelectCompany,
	setIsSelectCompany,
	patientId,
	...rest
}: IInnerProps) => {
	const props = useFormikContext<IAdvanceInvoiceModel>();
	const strings = useLocalization();
	useNextReferenceInterceptor(salesQuery_nextInvoiceReference, props);
	const [settings] = useApiEffect(salesQuery_settings);
	const [isUseContactPerson, setIsUseContactPerson] = useState<boolean>(false);
	useWithRecipientInterceptor<IAdvanceInvoiceModel>(props, isSelectCompany, isUseContactPerson, patientId);
	const { locations } = useFromLocationInterceptor<IAdvanceInvoiceModel>(props, patientId);

	if (settings === undefined) {
		return <div></div>;
	}

	return (
		<FullScreenDialogWithStepper
			{...rest}
			fullScreenStep={3}
			title={strings.newWhat(strings.advanceInvoice)}
			step={step}
			labels={[strings.referenceShort, strings.vatCustomer, strings.withRespectToShort, strings.items, strings.confirmation]}>
			<DialogContent
				dividers
				className='df-col fg1'>
				{step === 0 && (
					<>
						<FormSingleCheckboxField<IAdvanceInvoiceModel>
							name='isAutoSelectReference'
							label={strings.autoSelectIndexNumberQuestion}
						/>
						<FormTextField<IAdvanceInvoiceModel>
							name='reference'
							label={strings.invoiceNumber}
							disabled={props.values.isAutoSelectReference}
						/>
						<FormDatePickerWithUnit<IAdvanceInvoiceModel>
							name='validUntil'
							label={strings.validTo}
							required
							units={ValidUntilTimeUnits}
							defaultUnit='Months'
							defaultValue={settings.defaultValidUntil}
						/>
						<FormAutocompleteField<IAdvanceInvoiceModel, ILocationSummary>
							name='fromLocationId'
							label={strings.location}
							options={locations}
						/>
					</>
				)}
				{step === 1 && (
					<FormSelectOrCreateCompanyFieldWithSelector<IAdvanceInvoiceModel>
						patientId={patientId}
						isSelectCompany={isSelectCompany}
						setIsSelectCompany={setIsSelectCompany}
						disableSelector={patientId === undefined}
					/>
				)}
				{step === 2 && (
					<FormRecipientField<IAdvanceInvoiceModel>
						patientId={patientId}
						isUseContactPerson={isUseContactPerson}
						setIsUseContactPerson={setIsUseContactPerson}
					/>
				)}
				{step === 3 && (
					<SearchAndSelectProductsForAdvanceInvoice
						lines={lines}
						setLines={setLines}
					/>
				)}
				{step === 4 && (
					<SaleFormConfirmation
						lines={lines}
						reference={props.values.reference}
						recipientName={props.values.recipientName}
					/>
				)}
			</DialogContent>
			<PageableFormDialogActions
				step={step}
				setStep={setStep}
				cancel={cancel}
				isSubmitting={isSubmitting}
				submitText={strings.create}
				schema={schema}
				stepsRecord={stepsRecord}
			/>
		</FullScreenDialogWithStepper>
	);
};
